import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import Button from '@mui/material/Button';
import CopyrightIcon from '@mui/icons-material/Copyright';
import { CircularProgress, Fade, Grid, Typography, TextField, FormControlLabel, Switch } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Helper function to display field values with placeholder for null/empty
const displayValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return 'Not specified';
    }
    return value;
};

const CompanyEdit = ({ match }) => {
    // Session validation
    useSessionValidation();
    
    const { isAuthenticated, companyId, logout, token, userId } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [formProgress, setFormProgress] = useState(0);

    useEffect(() => {
        setMounted(true);
        // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }

    }, [isAuthenticated, companyId]);

    const [formData, setFormData] = useState([]);

    useEffect(() => {
        fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token)//, token
            .then((response) => {
                if (response.responseCode == '1111') {
                    setFormData(response.response_data);
                    setTimeout(() => calculateProgress(), 200);
                }
            })
            .catch((error) => {
                console.error('Error fetching Company Details:', error);
            });
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value === 'true' ? true : e.target.type === 'checkbox' ? e.target.checked : value,
        }));

        // Calculate form completion progress
        setTimeout(() => calculateProgress(), 100);
    };

    const calculateProgress = () => {
        const requiredFields = ['Name', 'ContactNumber1', 'AddressLine1', 'Location'];
        const optionalFields = ['GSTNumber', 'ContactNumber2', 'ContactNumber3', 'Email', 'AddressLine2', 'PostalCode', 'CashOpening', 'FreightGSTRate'];

        let filledRequired = 0;
        let filledOptional = 0;

        requiredFields.forEach(field => {
            if (formData[field] && formData[field].toString().trim() !== '') {
                filledRequired++;
            }
        });

        optionalFields.forEach(field => {
            if (formData[field] && formData[field].toString().trim() !== '') {
                filledOptional++;
            }
        });

        const requiredWeight = 70;
        const optionalWeight = 30;

        const progress = (filledRequired / requiredFields.length) * requiredWeight +
            (filledOptional / optionalFields.length) * optionalWeight;

        setFormProgress(Math.round(progress));
    };


    const handleSubmit = event => {
        event.preventDefault();
        setIsLoading(true);

        setFormData(prevData => ({
            ...prevData,
            UpdatedBy: userId,
        }));

        updateData('CompanyApi', `${companyId}`, formData, token)//, token
            .then((response) => {
                console.log('Record updated successfully:', response.data);
                window.location.reload();
            })
            .catch((error) => {
                console.error('Error updating record:', error);
                setIsLoading(false);
            });
    };

    return (
        <Fade in={mounted} timeout={800}>
            <div style={{
                minWidth: '100%',
                background: '#f5f7fa',
                padding: '10px',
                position: 'relative'
            }}>
                <style>{`
                    .premium-input {
                        transition: border-color 0.2s;
                        border: 1.5px solid #e5e7eb;
                        background: #ffffff;
                    }
                    .premium-input:focus {
                        border-color: #2C3E6E;
                        outline: none;
                        box-shadow: 0 0 0 3px rgba(44, 62, 110, 0.08);
                    }
                    .table-row:hover {
                        background: #f0f4ff !important;
                    }
                `}</style>

                <div style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    padding: '12px',
                    position: 'relative',
                    color: 'black',
                    fontFamily: 'Times New Roman',
                }}>
                    {/* Header Section */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #f0f0f0'
                    }}>
                        <h4 style={{
                            fontSize: '17px',
                            fontWeight: '700',
                            margin: 0,
                            letterSpacing: '-0.3px',
                            fontFamily: 'Times New Roman',
                            textTransform: 'uppercase',
                            color: 'black'
                        }}>
                            Company
                        </h4>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={isLoading}
                                startIcon={isLoading ? null : <SaveIcon style={{ fontSize: '14px' }} />}
                                style={{
                                    background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)',
                                    color: 'white',
                                    padding: '4px 12px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    boxShadow: '0 2px 8px rgba(44, 62, 110, 0.4)',
                                    border: 'none',
                                    minHeight: '29px',
                                    textTransform: 'none',
                                }}
                            >
                                {isLoading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
                            </Button>
                            <Button
                                variant="outlined"
                                component={Link}
                                to="/Home"
                                startIcon={<ArrowBackIcon style={{ fontSize: '14px' }} />}
                                style={{
                                    borderColor: '#2C3E6E',
                                    color: '#2C3E6E',
                                    padding: '4px 12px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    minHeight: '29px',
                                    textTransform: 'none',
                                }}
                            >
                                Dashboard
                            </Button>
                        </div>
                    </div>

                        {/* Company Configuration */}
                        <div style={{ marginBottom: '10px' }}>
                            <div>
                                <div style={{
                                    background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)',
                                    padding: '6px 12px',
                                    borderRadius: '8px 8px 0 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <BusinessIcon style={{ fontSize: '16px', color: 'white' }} />
                                    <span style={{
                                        color: 'white',
                                        fontWeight: '600',
                                        fontSize: '11px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Company Configuration
                                    </span>
                                </div>
                                <div style={{
                                    padding: '12px',
                                    border: '1px solid #e2e8f0',
                                    borderTop: 'none',
                                    borderRadius: '0 0 8px 8px',
                                    background: 'white',
                                }}>
                                    {(() => {
                                        const inputStyle = { borderRadius: '6px', fontSize: '13px', height: '32px' };
                                        const labelStyle = { fontSize: '11px' };
                                        const helperStyle = { fontSize: '9px', margin: '1px 0 0 0' };
                                        const sectionLabel = { fontWeight: '600', marginBottom: '4px', color: '#2d3748', fontSize: '12px', marginTop: '6px' };
                                        return (
                                            <Grid container spacing={1}>

                                                {/* Basic Info */}
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle1" style={sectionLabel}>Basic Information</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <TextField
                                                        name="CompanyName" label="Company Name" value={formData.CompanyName || ''}
                                                        onChange={handleChange} fullWidth size="small"
                                                        placeholder="Enter company name" inputProps={{ maxLength: 50 }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={2}>
                                                    <TextField
                                                        name="CompanyCode" label="Company Code" value={formData.CompanyCode || ''}
                                                        onChange={handleChange} fullWidth size="small"
                                                        placeholder="Code" inputProps={{ maxLength: 50 }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={2}>
                                                    <TextField
                                                        name="LicenseNo" label="License No" value={formData.LicenseNo || ''}
                                                        onChange={handleChange} fullWidth size="small"
                                                        placeholder="License number" inputProps={{ maxLength: 50 }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={2}>
                                                    <TextField
                                                        name="BillPrefix" label="Bill Prefix" value={formData.BillPrefix || ''}
                                                        onChange={handleChange} fullWidth size="small"
                                                        placeholder="e.g. INV" inputProps={{ maxLength: 20 }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={2}>
                                                    <TextField
                                                        name="ContactNumber" label="Contact Number" value={formData.ContactNumber || ''}
                                                        onChange={handleChange} fullWidth size="small"
                                                        placeholder="Contact number" inputProps={{ maxLength: 15 }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={12}>
                                                    <TextField
                                                        name="EmailId" label="Email ID" value={formData.EmailId || ''}
                                                        onChange={handleChange} fullWidth size="small"
                                                        placeholder="Email address" inputProps={{ maxLength: 50 }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>

                                                {/* Address */}
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle1" style={sectionLabel}>Address</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        name="Address1" label="Address Line 1" value={formData.Address1 || ''}
                                                        onChange={handleChange} fullWidth size="small"
                                                        placeholder="Street / Area" inputProps={{ maxLength: 200 }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        name="Address2" label="Address Line 2" value={formData.Address2 || ''}
                                                        onChange={handleChange} fullWidth size="small"
                                                        placeholder="City / State" inputProps={{ maxLength: 200 }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>

                                                {/* Postal Details */}
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle1" style={sectionLabel}>Postal Details</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <TextField
                                                        name="PostCode" label="Post Code" value={formData.PostCode || ''}
                                                        onChange={handleChange} fullWidth size="small"
                                                        placeholder="PIN code" inputProps={{ maxLength: 200 }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <TextField
                                                        name="PostHeadOffice" label="Head Office" value={formData.PostHeadOffice || ''}
                                                        onChange={handleChange} fullWidth size="small"
                                                        placeholder="Head office" inputProps={{ maxLength: 50 }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <TextField
                                                        name="PostPlace" label="Post Place" value={formData.PostPlace || ''}
                                                        onChange={handleChange} fullWidth size="small"
                                                        placeholder="Post place" inputProps={{ maxLength: 50 }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <TextField
                                                        name="District" label="District" value={formData.District || ''}
                                                        onChange={handleChange} fullWidth size="small"
                                                        placeholder="District" inputProps={{ maxLength: 50 }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <TextField
                                                        name="State" label="State" value={formData.State || ''}
                                                        onChange={handleChange} fullWidth size="small"
                                                        placeholder="State" inputProps={{ maxLength: 50 }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <TextField
                                                        name="Country" label="Country" value={formData.Country || ''}
                                                        onChange={handleChange} fullWidth size="small"
                                                        placeholder="Country" inputProps={{ maxLength: 50 }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>

                                                {/* Financial & Wastage */}
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle1" style={sectionLabel}>Financial & Wastage Settings</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <TextField
                                                        name="GSTRate" label="GST Rate (%)" value={formData.GSTRate ?? ''}
                                                        onChange={handleChange} fullWidth size="small" type="number"
                                                        placeholder="0.00" inputProps={{ step: '0.01' }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <TextField
                                                        name="TotalMargin" label="Total Margin (%)" value={formData.TotalMargin ?? ''}
                                                        onChange={handleChange} fullWidth size="small" type="number"
                                                        placeholder="0.00" inputProps={{ step: '0.01' }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <TextField
                                                        name="OpeningBalance" label="Opening Balance (₹)" value={formData.OpeningBalance ?? ''}
                                                        onChange={handleChange} fullWidth size="small" type="number"
                                                        placeholder="0.00" inputProps={{ step: '0.01' }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={3} style={{ display: 'flex', alignItems: 'center' }}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                name="IsPayable"
                                                                checked={formData.IsPayable || false}
                                                                onChange={handleChange}
                                                                size="small"
                                                                sx={{
                                                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#2C3E6E' },
                                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#2C3E6E' },
                                                                }}
                                                            />
                                                        }
                                                        label={<Typography style={{ fontSize: '12px', color: '#2d3748' }}>Is Payable</Typography>}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <TextField
                                                        name="TapeWastage" label="Tape Wastage (%)" value={formData.TapeWastage ?? ''}
                                                        onChange={handleChange} fullWidth size="small" type="number"
                                                        placeholder="0.00" inputProps={{ step: '0.01' }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <TextField
                                                        name="MountWastage" label="Mount Wastage (%)" value={formData.MountWastage ?? ''}
                                                        onChange={handleChange} fullWidth size="small" type="number"
                                                        placeholder="0.00" inputProps={{ step: '0.01' }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <TextField
                                                        name="FrameWastage" label="Frame Wastage (%)" value={formData.FrameWastage ?? ''}
                                                        onChange={handleChange} fullWidth size="small" type="number"
                                                        placeholder="0.00" inputProps={{ step: '0.01' }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <TextField
                                                        name="GLassWastage" label="Glass Wastage (%)" value={formData.GLassWastage ?? ''}
                                                        onChange={handleChange} fullWidth size="small" type="number"
                                                        placeholder="0.00" inputProps={{ step: '0.01' }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <TextField
                                                        name="BoardWastage" label="Board Wastage (%)" value={formData.BoardWastage ?? ''}
                                                        onChange={handleChange} fullWidth size="small" type="number"
                                                        placeholder="0.00" inputProps={{ step: '0.01' }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <TextField
                                                        name="LaminationWastage" label="Lamination Wastage (%)" value={formData.LaminationWastage ?? ''}
                                                        onChange={handleChange} fullWidth size="small" type="number"
                                                        placeholder="0.00" inputProps={{ step: '0.01' }}
                                                        InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                                                        FormHelperTextProps={{ style: helperStyle }}
                                                    />
                                                </Grid>

                                                {/* Sales Config removed */}

                                            </Grid>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    marginTop: '8px',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#6b7280'
                }}>
                    <CopyrightIcon style={{ fontSize: '13px' }} />
                    <span>EFFTABS SOLUTIONS LLP</span>
                </div>
                </div>{/* end white card */}
            </div>
        </Fade>
    );
};

export default CompanyEdit;
